import { Alert, Anchor, Button, Stack, TextInput } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { authApi } from '../api/auth';
import { errorMessage } from '../api/client';
import { forgotPasswordSchema, type ForgotPasswordValues } from '../auth/schemas';
import { AuthCard } from '../components/AuthCard';
import { ru } from '../i18n/ru';
import { accentText } from '../theme';

export function ForgotPasswordPage() {
  const [result, setResult] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      const response = await authApi.forgotPassword(values.email);
      setResult(response.message);
    } catch (error) {
      setFailure(errorMessage(error));
    }
  });

  return (
    <AuthCard title={ru.auth.forgotTitle} description={ru.auth.forgotHint}>
      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Stack gap="md">
          {failure !== null && (
            <Alert color="red" title={ru.common.error}>
              {failure}
            </Alert>
          )}

          {result !== null ? (
            <Alert color="green">{result}</Alert>
          ) : (
            <>
              <TextInput
                label={ru.fields.email}
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                {...register('email')}
              />
              <Button type="submit" loading={isSubmitting}>
                {ru.auth.forgotSubmit}
              </Button>
            </>
          )}

          <Anchor component={Link} to="/login" size="sm" c={accentText}>
            {ru.auth.goToLogin}
          </Anchor>
        </Stack>
      </form>
    </AuthCard>
  );
}
